const DOMAIN_TO_BUCKET: Array<[string[], string]> = [
  [["l.instagram.com", "instagram.com", "www.instagram.com"], "Instagram"],
  [
    [
      "threads.net",
      "www.threads.net",
      "l.threads.net",
      "thread.net",
      "www.thread.net",
    ],
    "Instagram",
  ],
  [
    [
      "facebook.com",
      "m.facebook.com",
      "l.facebook.com",
      "lm.facebook.com",
      "www.facebook.com",
    ],
    "Facebook",
  ],
  [["www.tiktok.com", "tiktok.com"], "TikTok"],
  [["youtube.com", "www.youtube.com", "m.youtube.com"], "YouTube"],
  [["t.co", "twitter.com", "x.com", "www.x.com"], "X (Twitter)"],
  [["linktr.ee", "beacons.ai", "bit.ly", "link.me"], "Link in bio"],
];

const GOOGLE_PATTERNS = [
  "google.com",
  "google.co.",
  "www.google.",
  "android-app://com.google",
  "com.google.android.gm",
  "com.google.android.googlequicksearchbox",
];

const domainCache = new Map<string, string>();

function matchDomain(domain: string): string {
  for (const [domains, bucket] of DOMAIN_TO_BUCKET) {
    if (domains.includes(domain)) return bucket;
  }
  for (const pattern of GOOGLE_PATTERNS) {
    if (domain.includes(pattern)) return "Google";
  }
  return "Your site / Other";
}

function extractDomainFromUrl(url: string): string {
  try {
    const withProtocol = url.startsWith("http") ? url : `https://${url}`;
    return new URL(withProtocol).hostname;
  } catch {
    return url;
  }
}

export function bucketReferrer(
  initialReferringDomain: string | null,
  initialReferrer: string | null
): string {
  const raw = initialReferringDomain || initialReferrer;

  if (!raw || raw === "$direct" || raw === "" || raw === "undefined") {
    return "Came straight to Punchup";
  }

  const cached = domainCache.get(raw);
  if (cached) return cached;

  const domain = initialReferringDomain
    ? raw.toLowerCase()
    : extractDomainFromUrl(raw).toLowerCase();

  const bucket = matchDomain(domain);
  domainCache.set(raw, bucket);
  return bucket;
}

export const SOURCE_BUCKETS = [
  "Came straight to Punchup",
  "Instagram",
  "Facebook",
  "TikTok",
  "YouTube",
  "X (Twitter)",
  "Google",
  "Link in bio",
  "Your site / Other",
] as const;

export type SourceBucket = (typeof SOURCE_BUCKETS)[number];

const LEGACY_OWN_SITE_BUCKET = "Own site / Other";
const CURRENT_OWN_SITE_BUCKET = "Your site / Other";

/**
 * Rewrites the legacy "Own site / Other" key to "Your site / Other" when
 * rendering historical digest rows. Bucket labels are stored as JSON keys
 * inside `follow_source_breakdown` / `ticket_source_breakdown`, so rows
 * generated before the rename still carry the old key. If both keys
 * happen to be present (unexpected), the legacy entry is dropped to
 * avoid a duplicate row in the rendered table.
 */
export function relabelLegacyBuckets<T>(
  breakdown: Record<string, T>
): Record<string, T> {
  if (!(LEGACY_OWN_SITE_BUCKET in breakdown)) return breakdown;
  const { [LEGACY_OWN_SITE_BUCKET]: legacy, ...rest } = breakdown;
  if (CURRENT_OWN_SITE_BUCKET in rest) return rest;
  return { ...rest, [CURRENT_OWN_SITE_BUCKET]: legacy };
}
